import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import ExposureIcon from '@mui/icons-material/Exposure';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import {
  ROUTE_CASES,
  ROUTE_TREATMENTS,
  ROUTE_DRUGS,
  ROUTE_CALCULATOR,
  ROUTE_PROFILE_INFO,
  ROUTE_SUPPORT,
  ROUTE_TUTORIAL,
} from '../../config/routes.config';
import { useAuthUser } from '../../contexts/AuthUserContext';
import DiagnosticsIcon from '../../assets/icons/DiagnosticsIcon';
import DiseasesIcon from '../../assets/icons/DiseasesIcon';
import DrugsIcon from '../../assets/icons/DrugsIcon';
import FeedbackIcon from '../../assets/icons/FeedbackIcon';

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: 280,
  },
  drawerPaper: {
    width: 280,
    paddingTop: theme.spacing(8),
  },
  listItem: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  listItemActive: {
    backgroundColor: theme.palette.action.selected,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  listItemIcon: {
    minWidth: 40,
    color: '#444444',
  },
}));

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MobileNavigation = ({
  isOpen,
  onClose,
}: MobileNavigationProps) => {
  const { classes, cx } = useStyles();
  const location = useLocation();
  const { signOut } = useAuthUser();
  const [toolsOpen, setToolsOpen] = useState(false);

  const isCasesActive = location.pathname.startsWith('/cases');
  const isToolsActive = location.pathname.startsWith('/tools');

  const handleToolsClick = () => {
    setToolsOpen(!toolsOpen);
  };

  const handleNavClick = () => {
    onClose();
  };

  const handleLogout = () => {
    onClose();
    signOut();
  };

  const toolsMenuItems = [
    { title: 'Diseases', to: ROUTE_TREATMENTS, icon: <DiseasesIcon /> },
    { title: 'Drug Substances', to: ROUTE_DRUGS, icon: <DrugsIcon /> },
    {
      title: 'Drug Dosage Calculator',
      to: ROUTE_CALCULATOR,
      icon: <ExposureIcon />,
    },
  ];

  const profileMenuItems = [
    {
      title: 'My Profile',
      to: ROUTE_PROFILE_INFO,
      icon: <PersonOutlineIcon />,
    },
    { title: 'Support', to: ROUTE_SUPPORT, icon: <FeedbackIcon /> },
    { title: 'Tutorial', to: ROUTE_TUTORIAL, icon: <HelpOutlineIcon /> },
  ];

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <List>
        {/* Diagnostics */}
        <ListItem
          button
          component={Link}
          to={ROUTE_CASES}
          onClick={handleNavClick}
          className={cx(
            classes.listItem,
            isCasesActive && classes.listItemActive
          )}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <DiagnosticsIcon />
          </ListItemIcon>
          <ListItemText primary="Diagnostics" />
        </ListItem>

        {/* Tools dropdown */}
        <ListItem
          button
          onClick={handleToolsClick}
          className={cx(
            classes.listItem,
            isToolsActive && classes.listItemActive
          )}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <BuildIcon />
          </ListItemIcon>
          <ListItemText primary="Tools" />
          {toolsOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={toolsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {toolsMenuItems.map((item) => (
              <ListItem
                key={item.to}
                button
                component={Link}
                to={item.to}
                onClick={handleNavClick}
                className={cx(
                  classes.nested,
                  location.pathname === item.to && classes.listItemActive
                )}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItem>
            ))}
          </List>
        </Collapse>

        <Divider className={classes.divider} />

        {/* Profile items */}
        {profileMenuItems.map((item) => (
          <ListItem
            key={item.to}
            button
            component={Link}
            to={item.to}
            onClick={handleNavClick}
            className={cx(
              classes.listItem,
              location.pathname === item.to && classes.listItemActive
            )}
          >
            <ListItemIcon className={classes.listItemIcon}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.title} />
          </ListItem>
        ))}

        <Divider className={classes.divider} />

        {/* Logout */}
        <ListItem button onClick={handleLogout} className={classes.listItem}>
          <ListItemIcon className={classes.listItemIcon}>
            <PowerSettingsNewIcon />
          </ListItemIcon>
          <ListItemText primary="Log out" />
        </ListItem>
      </List>
    </Drawer>
  );
};

export default MobileNavigation;
